import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MAINTENANCE } from "@/constants/testIds";

const empty = () => ({
  property_id: "", provider_id: "",
  type: "repair", title: "", description: "",
  status: "scheduled",
  scheduled_date: new Date().toISOString().slice(0, 10),
  completed_date: "",
  cost: "", currency: "USD", warranty_until: "",
});

export default function MaintenanceDialog({ open, onOpenChange, properties, providers, item, onSaved, defaultPropertyId }) {
  const [form, setForm] = useState(empty());

  useEffect(() => {
    if (item) {
      setForm({ ...empty(), ...item, cost: item.cost ?? "" });
    } else if (open) {
      const f = empty();
      if (defaultPropertyId) {
        f.property_id = defaultPropertyId;
        const p = properties.find((x) => x.id === defaultPropertyId);
        if (p) f.currency = p.currency;
      } else if (properties.length) {
        f.property_id = properties[0].id;
        f.currency = properties[0].currency;
      }
      setForm(f);
    }
  }, [open, item, properties, defaultPropertyId]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        cost: parseFloat(form.cost) || 0,
        provider_id: form.provider_id || null,
        scheduled_date: form.scheduled_date || null,
        completed_date: form.completed_date || null,
        warranty_until: form.warranty_until || null,
      };
      if (item?.id) {
        await api.put(`/maintenance/${item.id}`, payload);
        toast.success("Mantenimiento actualizado");
      } else {
        await api.post("/maintenance", payload);
        toast.success("Mantenimiento registrado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={MAINTENANCE.dialog} className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? "Editar mantenimiento" : "Nuevo mantenimiento"}</DialogTitle>
          <DialogDescription>Repare, prevenga o mejore. El costo se convierte a USD.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Título</Label>
            <Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="maintenance-form-title" />
          </div>
          <div>
            <Label>Propiedad</Label>
            <Select value={form.property_id} onValueChange={(v) => {
              set("property_id", v);
              const p = properties.find((x) => x.id === v);
              if (p) set("currency", p.currency);
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Proveedor (opcional)</Label>
            <Select value={form.provider_id || "none"} onValueChange={(v) => set("provider_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguno</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="repair">Reparación</SelectItem>
                <SelectItem value="preventive">Preventivo</SelectItem>
                <SelectItem value="inspection">Inspección</SelectItem>
                <SelectItem value="improvement">Mejora</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Programado</SelectItem>
                <SelectItem value="in_progress">En curso</SelectItem>
                <SelectItem value="done">Finalizado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fecha programada</Label>
            <Input type="date" value={form.scheduled_date || ""} onChange={(e) => set("scheduled_date", e.target.value)} />
          </div>
          <div>
            <Label>Fecha completado</Label>
            <Input type="date" value={form.completed_date || ""} onChange={(e) => set("completed_date", e.target.value)} />
          </div>
          <div>
            <Label>Costo</Label>
            <Input type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} />
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="MXN">MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Garantía hasta</Label>
            <Input type="date" value={form.warranty_until || ""} onChange={(e) => set("warranty_until", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} />
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="maintenance-form-submit">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
